import React, { FC, useMemo } from 'react';
import { Box, keyframes } from '@mui/material';
import { ChristmasColors } from './christmasColors';

/** Bulb colours, repeated along the string. */
const BULB_COLORS = [
  ChristmasColors.BAUBLE_RED,
  ChristmasColors.BAUBLE_GOLD,
  ChristmasColors.BAUBLE_BLUE,
  ChristmasColors.TREE_LIGHT,
  ChristmasColors.GLOW,
];

/** Bulbs twinkle in this many groups, each offset in time, so the string never blinks in unison. */
const TWINKLE_GROUPS = 4;

// Everything fits in the header's top padding, so the lights never cover buttons or the page below.
const HEIGHT = 12;
const WIRE_TOP = 1;
const WIRE_SAG_CONTROL = 5; // control point of the wire's curve; the wire itself sags to y = 3
const SOCKET_TOP = 2.5;
const BULB_Y = 7.5;
/** The string fades out towards both ends of the header instead of running edge to edge. */
const EDGE_FADE_PX = 64;

const twinkle = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

export interface ChristmasLightsProps {
  /** Number of bulbs. Together with `spacing` this decides how wide a header the string can cover. */
  count?: number;
  /** Distance between bulbs in px. */
  spacing?: number;
  /** Twinkle quickly (used while the easter egg is running). */
  fast?: boolean;
}

/**
 * A thin string of fairy lights along the top edge of the header. It is small enough to sit in the header's
 * own padding, so it never covers the header's buttons or the page below it. Positioned absolutely, so the
 * header (`MuiAppBar`) must be a positioned ancestor; the Christmas theme takes care of that via `extendMuiTheme`.
 * Purely decorative: hidden from assistive tech and transparent to the pointer.
 */
export const ChristmasLights: FC<ChristmasLightsProps> = ({ count = 130, spacing = 28, fast = false }) => {
  const width = count * spacing;

  const wirePath = useMemo(() => {
    const segments = Array.from({ length: count }, (_, i) => {
      const x = i * spacing;
      return `Q${x + spacing / 2} ${WIRE_SAG_CONTROL} ${x + spacing} ${WIRE_TOP}`;
    });
    return `M0 ${WIRE_TOP} ${segments.join(' ')}`;
  }, [count, spacing]);

  const bulbs = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        x: i * spacing + spacing / 2,
        color: BULB_COLORS[i % BULB_COLORS.length],
        // Spread the groups out along the string instead of in order, so neighbours rarely blink together.
        group: (i * 3) % TWINKLE_GROUPS,
      })),
    [count, spacing],
  );

  const duration = fast ? 0.6 : 2.2;

  return (
    <Box
      aria-hidden
      data-testid='ChristmasLights'
      data-fast={fast ? 'true' : 'false'}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: HEIGHT,
        maskImage: `linear-gradient(to right, transparent, #000 ${EDGE_FADE_PX}px, #000 calc(100% - ${EDGE_FADE_PX}px), transparent)`,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1,
        lineHeight: 0,
        // The keyframes must be referenced from `sx` (Emotion) to be injected; per-bulb values go in `style`.
        '& .bulb': {
          animation: `${twinkle} ${duration}s ease-in-out infinite`,
          transformOrigin: 'center',
        },
        '@media (prefers-reduced-motion: reduce)': { '& .bulb': { animation: 'none' } },
      }}>
      <svg width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} role='img' aria-hidden='true'>
        {/* the wire */}
        <path d={wirePath} fill='none' stroke={ChristmasColors.LIGHT_WIRE} strokeWidth='1' />
        {bulbs.map((bulb) => (
          <g
            key={bulb.id}
            className='bulb'
            data-testid='ChristmasLightBulb'
            style={{ animationDelay: `${(bulb.group * duration) / TWINKLE_GROUPS}s` }}>
            {/* glow */}
            <circle cx={bulb.x} cy={BULB_Y} r='4' fill={bulb.color} opacity='0.3' />
            {/* socket */}
            <rect
              x={bulb.x - 1.3}
              y={SOCKET_TOP}
              width='2.6'
              height='2.6'
              rx='0.5'
              fill={ChristmasColors.LIGHT_SOCKET}
            />
            {/* bulb */}
            <ellipse cx={bulb.x} cy={BULB_Y} rx='1.9' ry='2.8' fill={bulb.color} />
            {/* highlight */}
            <ellipse cx={bulb.x - 0.7} cy={BULB_Y - 1} rx='0.5' ry='0.9' fill={ChristmasColors.SNOW} opacity='0.55' />
          </g>
        ))}
      </svg>
    </Box>
  );
};

export default ChristmasLights;
